import { isDevMode, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { DatePipe, registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import en from '@angular/common/locales/en';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  TranslateCompiler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CoreModule } from '@core/core.module';
import { ErrorInterceptor } from '@root/app/core/interceptor/error.interceptor';
import { AppStorage } from '@shared/storage/app-storage';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderInterceptor } from '@core/interceptor/header.interceptor';
import { ViewLoaderComponent } from '@shared/components/view-loader/view-loader.component';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { ErrorPageComponent } from '@root/app/error-page/error-page.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AuthInterceptor } from '@core/auth/interceptors/auth.interceptor';
import { LoadingInterceptor } from '@core/interceptor/loading.interceptor';
import {
  EmployeeImageByIdGetter,
  EmployeeImageToken,
} from '@shared/attachments/employee-image.token';
import { AccessControlUserRepositoryService } from '@shared/access-control-users/data-access/services/access-control-user-repository.service';
import { NetworkLogInterceptor } from '@root/app/error-aggregation/network-log.interceptor';
import { MAT_EXPANSION_PANEL_DEFAULT_OPTIONS } from '@angular/material/expansion';

registerLocaleData(en);

function httpLoaderFactory(httpBackend: HttpBackend) {
  return new TranslateHttpLoader(
    new HttpClient(httpBackend),
    './assets/i18n/',
    `.json?v=${new Date().getTime()}`
  );
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    CoreModule,
    /*
     * TranslateModule.forRoot must be called only on app module level
     * root module must  isolate to prevent child lazy loaded module from override app module translation
     * */
    TranslateModule.forRoot({
      defaultLanguage: AppStorage.getLanguage(),
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpBackend],
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler,
      },
      isolate: false,
    }),
    ViewLoaderComponent,
    ErrorPageComponent,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately',
    }),
  ],
  providers: [
    DatePipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NetworkLogInterceptor,
      multi: true,
    },
    {
      provide: EmployeeImageToken,
      useFactory:
        (
          service: AccessControlUserRepositoryService
        ): EmployeeImageByIdGetter =>
        (employeeId: string) =>
          service.getUserPhoto(employeeId),
      deps: [AccessControlUserRepositoryService],
    },
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: MAT_EXPANSION_PANEL_DEFAULT_OPTIONS,
      useValue: {
        collapsedHeight: 'auto',
      },
    },
  ],
})
export class AppModule {}
